/**
 * BT-039 / BT-040 — Dictée vocale sans duplication + mode continu.
 */

export function isSpeechRecognitionSupported() {
  if (typeof globalThis === "undefined") return false;
  return Boolean(globalThis.SpeechRecognition || globalThis.webkitSpeechRecognition);
}

/**
 * Fusionne texte de base + finals accumulés + interim courant (sans répétition).
 */
export function mergeDictationText(baseText, finalSegment, interimSegment) {
  const parts = [baseText?.trim(), finalSegment?.trim(), interimSegment?.trim()].filter(Boolean);
  return parts.join(" ");
}

/**
 * Détecte répétition évidente (phrase dupliquée consécutive).
 */
export function hasObviousTextRepetition(text) {
  if (!text || text.length < 4) return false;
  const normalized = text.replace(/\s+/g, " ").trim();
  if (/(\b[\wàâäéèêëïîôùûüç'-]+\b)(\s+\1\b)+/iu.test(normalized)) return true;
  const half = Math.floor(normalized.length / 2);
  for (let len = 4; len <= half; len += 1) {
    const chunk = normalized.slice(0, len);
    if (normalized.slice(len, len * 2) === chunk && chunk.length > 3) return true;
  }
  const words = normalized.split(" ");
  for (let i = 0; i < words.length - 3; i += 1) {
    const triplet = words.slice(i, i + 3).join(" ");
    const next = words.slice(i + 3, i + 6).join(" ");
    if (triplet === next && triplet.length > 5) return true;
  }
  return false;
}

/** Erreurs fatales — ne pas redémarrer après silence. */
export function isFatalSpeechError(errorCode) {
  return errorCode === "not-allowed" || errorCode === "service-not-allowed" || errorCode === "audio-capture";
}

/** Erreurs silence / pause — redémarrer si mode actif (BT-040). */
export function shouldRestartAfterSpeechError(errorCode) {
  return errorCode === "no-speech";
}

/**
 * Crée une session SpeechRecognition avec gestion interim/final correcte.
 */
export function createDictationSession({ locale, baseText, onText, onEnd, onError }) {
  const SpeechRecognition = globalThis.SpeechRecognition || globalThis.webkitSpeechRecognition;
  if (!SpeechRecognition) return null;

  const rec = new SpeechRecognition();
  rec.lang = locale === "en" ? "en-CA" : "fr-CA";
  rec.interimResults = true;
  rec.continuous = true;

  let finalTranscript = "";

  rec.onresult = (event) => {
    let interimTranscript = "";
    for (let i = event.resultIndex; i < event.results.length; i += 1) {
      const piece = event.results[i][0].transcript;
      if (event.results[i].isFinal) {
        finalTranscript += piece;
      } else {
        interimTranscript += piece;
      }
    }
    const merged = mergeDictationText(baseText, finalTranscript, interimTranscript);
    if (!hasObviousTextRepetition(merged)) {
      onText(merged);
    } else {
      onText(mergeDictationText(baseText, finalTranscript, ""));
    }
  };

  rec.onend = () => onEnd?.();
  rec.onerror = (event) => onError?.(event);

  return rec;
}

const RESTART_DELAY_MS = 80;

/**
 * Contrôleur dictée continue — reste actif jusqu'au stop utilisateur (BT-040).
 * @param {{ locale: string, getBaseText: () => string, onText: (text: string) => void, onActiveChange?: (active: boolean) => void }} opts
 */
export function createContinuousDictationController({ locale, getBaseText, onText, onActiveChange }) {
  let userActive = false;
  let recognition = null;
  let restartTimer = null;

  function clearRestartTimer() {
    if (restartTimer != null) {
      clearTimeout(restartTimer);
      restartTimer = null;
    }
  }

  function startSession() {
    if (!userActive) return false;
    const rec = createDictationSession({
      locale,
      baseText: getBaseText(),
      onText,
      onEnd: handleEnd,
      onError: handleError,
    });
    if (!rec) return false;
    recognition = rec;
    try {
      rec.start();
      return true;
    } catch {
      if (userActive) {
        restartTimer = setTimeout(() => startSession(), RESTART_DELAY_MS);
      }
      return false;
    }
  }

  function handleEnd() {
    recognition = null;
    if (!userActive) return;
    clearRestartTimer();
    restartTimer = setTimeout(() => {
      if (userActive) startSession();
    }, RESTART_DELAY_MS);
  }

  function handleError(event) {
    const code = event?.error ?? "";
    recognition = null;
    if (!userActive) return;
    if (isFatalSpeechError(code)) {
      userActive = false;
      clearRestartTimer();
      onActiveChange?.(false);
      return;
    }
    if (shouldRestartAfterSpeechError(code) || code === "network") {
      handleEnd();
    }
  }

  return {
    start() {
      userActive = true;
      clearRestartTimer();
      onActiveChange?.(true);
      startSession();
    },
    stop() {
      userActive = false;
      clearRestartTimer();
      try {
        recognition?.stop();
      } catch {
        /* ignore */
      }
      recognition = null;
      onActiveChange?.(false);
    },
    isUserActive() {
      return userActive;
    },
  };
}

/**
 * Mock simple — une phrase puis onend (BT-039).
 */
export function installSpeechRecognitionMock() {
  class MockSpeechRecognition {
    lang = "fr-CA";
    interimResults = true;
    continuous = true;
    onresult = null;
    onend = null;
    onerror = null;

    start() {
      const phrase = "Phrase test unique";
      queueMicrotask(() => {
        this.onresult?.({
          resultIndex: 0,
          results: [{ 0: { transcript: phrase }, isFinal: false, length: 1 }],
        });
        this.onresult?.({
          resultIndex: 0,
          results: [{ 0: { transcript: phrase }, isFinal: true, length: 1 }],
        });
        this.onend?.();
      });
    }

    stop() {
      this.onend?.();
    }
  }
  globalThis.SpeechRecognition = MockSpeechRecognition;
  globalThis.webkitSpeechRecognition = MockSpeechRecognition;
  return MockSpeechRecognition;
}

/**
 * Mock continu — simule silence/onend puis reprise (BT-040).
 */
export function installContinuousSpeechRecognitionMock() {
  let instanceCount = 0;

  class MockContinuousSpeechRecognition {
    lang = "fr-CA";
    interimResults = true;
    continuous = true;
    onresult = null;
    onend = null;
    onerror = null;

    start() {
      instanceCount += 1;
      const id = instanceCount;
      const segments =
        id === 1
          ? ["Première partie"]
          : id === 2
            ? [" deuxième partie"]
            : [" suite"];
      for (const seg of segments) {
        this.onresult?.({
          resultIndex: 0,
          results: [{ 0: { transcript: seg }, isFinal: true, length: 1 }],
        });
      }
      this.onend?.();
    }

    stop() {
      this.onend?.();
    }
  }

  globalThis.SpeechRecognition = MockContinuousSpeechRecognition;
  globalThis.webkitSpeechRecognition = MockContinuousSpeechRecognition;
  globalThis.__speechMockInstanceCount = () => instanceCount;
  return MockContinuousSpeechRecognition;
}
